/* eslint-disable max-lines */
import {
    CardDistributionType,
    CardEnum,
    CityImprovementTypes,
    GameLogAchievementEnum,
    GameLogData,
    GameLogKnightInformation,
    GameLogPieceEnum,
    GameLogTileInformation,
    GameLogType,
    PlayerColor,
    TileEdgeType,
    TileHexType,
} from '@colonist/game-states'
import {FeedMessageData} from './FeedMessageData'
import {getNestedTranslation, PlayerDisplayName, PLAYER_DISPLAY_NAME_END, PLAYER_DISPLAY_NAME_START, GameModeTranslationKeyContext} from '@colonist/i18n'
import {ModeSettingEnum, SharedStrings, LobbyImage} from '@colonist/types'
import {EnumUtils} from '@colonist/utils'
// eslint-disable-next-line no-restricted-imports
import {getMerchantImageNameForPlayer} from '../../../../../../code/client/shared/scripts/ui_game/UIGameImageDictionary'
import {GameValidator, PlayerValidator} from '@colonist/game-mechanic-validators'
// eslint-disable-next-line no-restricted-imports
import {uiGameManager} from '../../../../../../code/client/shared/scripts/EntryShared'
import {CARD_TIP_TITLE} from '@colonist/ui-game'
// eslint-disable-next-line no-restricted-imports
import {clientI18n} from '../../../../../../code/client/shared/scripts/ClientI18n'

export const CARD_TOOLTIP_LINK_PREFIX = '#card-description-popup-'

function getPlayerUsernameByColor(gameValidator: GameValidator, color: PlayerColor): string {
    return gameValidator.getPlayerValidator(color).username
}

function getPlayerDisplayNameByColor(gameValidator: GameValidator, color: PlayerColor): PlayerDisplayName {
    const username = getPlayerUsernameByColor(gameValidator, color)
    return `${PLAYER_DISPLAY_NAME_START}${username}${PLAYER_DISPLAY_NAME_END}`
}

function getUsernamesByColors(gameValidator: GameValidator, playerColors: PlayerColor[]): string {
    const usernames: string[] = []
    for(const playerColor of playerColors) {
        usernames.push(getPlayerUsernameByColor(gameValidator, playerColor))
    }
    // regex, replace last comma with ' and '
    return usernames.join(', ').replace(/,(?=[^,]*$)/, ' and ')
}

function getPieceStringFromPieceEnum(pieceEnum: GameLogPieceEnum): string {
    switch(pieceEnum) {
        case GameLogPieceEnum.Road: return `${clientI18n.t('strings:game.PieceTypes.Road')} :road:`
        case GameLogPieceEnum.Ship: return `${clientI18n.t('strings:game.PieceTypes.Ship')} :ship:`
        case GameLogPieceEnum.Settlement: return `${clientI18n.t('strings:game.PieceTypes.Settlement')} :settlement:`
        case GameLogPieceEnum.City: return `${clientI18n.t('strings:game.PieceTypes.City')} :city:`
        case GameLogPieceEnum.CityWall: return `${clientI18n.t('strings:game.PieceTypes.CityWall')} :city_wall:`
        case GameLogPieceEnum.Robber: return `${clientI18n.t('strings:imgAlts.Robber')} :robber:`
        case GameLogPieceEnum.Pirate: return `${clientI18n.t('strings:imgAlts.IconPirateShip')} :pirate:`
        default: return ''
    }
}

function getKnightImageName(knightInfo: GameLogKnightInformation): string {
    const activationString = knightInfo.isActivated ? 'active' : 'inactive'
    const playerColor = PlayerColor[knightInfo.ownerColor].toLocaleLowerCase()
    return `knight_${activationString}_level${knightInfo.level}_${playerColor}`
}

function getDiceString(firstDice: number, secondDice: number, eventDice: number | undefined): string {
    const isRedDiceActive = eventDice !== undefined
    const firstDiceImage = isRedDiceActive ? `:dice_red:${firstDice}` : `:dice:${firstDice}`
    const diceString = `${firstDiceImage} :dice:${secondDice}`
    if(eventDice == undefined) return diceString
    return diceString + ` :partition: :dice_event:${eventDice}`
}

function getTileChatString(tileInfo: GameLogTileInformation): string {
    let resourceCardType: string = ''

    if(tileInfo.resourceType != undefined) resourceCardType = CardEnum[tileInfo.resourceType]
    if(tileInfo.tileType == TileHexType.Gold) resourceCardType = 'Gold'
    if(tileInfo.tileType == TileHexType.Desert) resourceCardType = 'Desert'

    const chatString = tileInfo.diceNumber != 0 ? ` :prob:${tileInfo.diceNumber} :tile_${resourceCardType}:` : ` ${resourceCardType}`

    return chatString
}

function getAchievementString(achievementEnum: GameLogAchievementEnum, translationContext: GameModeTranslationKeyContext): string {
    switch(achievementEnum) {
        case GameLogAchievementEnum.LargestArmy: return `${clientI18n.t('strings:game.playerInformation.largestArmy.title', {context: translationContext})} :largestarmy:`
        case GameLogAchievementEnum.LongestRoad: return `${clientI18n.t('strings:game.playerInformation.longestRoad.title', {context: translationContext})} :longestroad:`
    }
}

function getCityImprovementFramedSquareImageName(improvementType: CityImprovementTypes): string {
    switch(improvementType) {
        case CityImprovementTypes.Politics: return 'icon_square_framed_politics'
        case CityImprovementTypes.Trade: return 'icon_square_framed_trade'
        case CityImprovementTypes.Science: return 'icon_square_framed_science'
    }
}

function getBotIsPlacingContext(pieceEnum: GameLogPieceEnum.Road | GameLogPieceEnum.Ship): string {
    switch(pieceEnum) {
        case GameLogPieceEnum.Road: return 'road'
        case GameLogPieceEnum.Ship: return 'ship'
    }
}

export function createFeedMessageDataForGameLogData(gameValidator: GameValidator, gameLogData: GameLogData, translationKeyContext: GameModeTranslationKeyContext, playerValidator?: PlayerValidator): FeedMessageData {
    const chatData = createFeedMessageDataForGameLogDataHelper(gameValidator, gameLogData, translationKeyContext)
    if(gameLogData.from == undefined) return chatData

    if(chatData.colorAndBoldUsernames == undefined) chatData.colorAndBoldUsernames = true

    if(!playerValidator) {
        playerValidator = gameValidator.getPlayerValidator(gameLogData.from)
    }

    if(chatData.icon == undefined) chatData.icon = playerValidator.displayIcon
    if(chatData.profilePictureUrl == undefined) chatData.profilePictureUrl = playerValidator.profilePictureUrl

    return chatData
}

function createFeedMessageDataForGameLogDataHelper(gameValidator: GameValidator, gameLogData: GameLogData, translationKeyContext: GameModeTranslationKeyContext): FeedMessageData {
    switch(gameLogData.text.type) {
        case GameLogType.PlayerReconnecting:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerReconnected',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                    },
                },
                italics: true,
            }
        case GameLogType.BoughtDevelopmentCard:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerBoughtCard',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardString: convertCardsToChatImageString([CardEnum.DevelopmentBack]),
                    },
                },
            }
        case GameLogType.WelcomeMessage:
            const context = gameLogData.text.isDiscord ? 'discord' : undefined
            return {
                text: {key: 'strings:homePage.header.welcomeMessage', options: {context: context, helpCommand: '/help'}},
            }
        case GameLogType.StartedSpectating:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.userStartedSpectating',
                    options: {username: gameLogData.text.username},
                },
            }
        case GameLogType.PlayerPlacedPiece:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerPlacedPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                    },
                },
            }
        case GameLogType.BuiltPiece:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerBuiltPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                        context: gameLogData.text.isVp ? 'vp' : undefined,
                    },
                },
            }
        case GameLogType.PlayerMovedPiece:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerMovedPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                        context: gameLogData.text.pieceEnum == GameLogPieceEnum.Pirate ? 'colon' : undefined,
                    },
                },
            }
        case GameLogType.UpgradedKnight:
            const oldKnightImageName = getKnightImageName(gameLogData.text.oldKnightInfo)
            const newKnightImageName = getKnightImageName(gameLogData.text.newKnightInfo)
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerUpgradedPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        oldPieceString: `:${oldKnightImageName}:`,
                        newPieceString: `:${newKnightImageName}:`,
                    },
                },
            }
        case GameLogType.ActivatedKnight:
            const activatedKnightImageName = getKnightImageName(gameLogData.text.knightInfo)
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerActivatedKnight',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: `:${activatedKnightImageName}:`,
                    },
                },
            }
        case GameLogType.RemovedKnight:
            const removedKnightImageName = getKnightImageName(gameLogData.text.knightInfo)
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerRemovedKnight',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: `:${removedKnightImageName}:`,
                    },
                },
            }
        case GameLogType.RolledDice:
            const diceString = getDiceString(gameLogData.text.firstDice,
                gameLogData.text.secondDice,
                gameLogData.text.eventDice)
            const isRushMode = uiGameManager.gameController.gameSettings.modeSetting === ModeSettingEnum.ColonistRush4P
            return {
                text: {
                    key: isRushMode ? 'strings:game.gameState.gameLogs.diceRolled' : 'strings:game.gameState.gameLogs.playerRolledDice',
                    options: isRushMode ? {
                        diceString: diceString,
                    } : {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        diceString: diceString,
                    },
                },
                icon: isRushMode ? LobbyImage.Lightning : undefined,
            }
        case GameLogType.MovedRobber:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerMovedRobber',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                        tileChatString: getTileChatString(gameLogData.text.tileInfo),
                    },
                },
            }
        case GameLogType.PlacedMetropolis:
            const placedMetropolisText = EnumUtils.getEnumKey(gameLogData.text.metropolisType, CityImprovementTypes)
            return {
                text: {
                    key: `strings:game.gameState.gameLogs.playerPlacedMetropolis.${placedMetropolisText}`,
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: `:metropolis_${placedMetropolisText}:`,
                    },
                },
            }
        case GameLogType.ReceivedCard:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerReceived',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: convertCardsToChatImageString([gameLogData.text.cardEnum]),
                    },
                },
            }
        case GameLogType.StolenResourceCardThief:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.stolenResourceCards.thief',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardString: convertCardsToChatImageString(gameLogData.text.cardEnums),
                    },
                },
            }
        case GameLogType.StolenResourceCardVictim:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.stolenResourceCards.victim',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardString: convertCardsToChatImageString(gameLogData.text.cardEnums),
                    },
                },
            }
        case GameLogType.StolenResourceCardClosed:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.stolenResourceCards.closed',
                    options: {
                        playerNameThief: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorThief),
                        playerNameVictim: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorVictim),
                        cardString: convertCardsToChatImageString(gameLogData.text.cardBacks),
                    },
                },
            }
        case GameLogType.StolenDevelopmentCardThief:
            return {
                text: getNestedTranslation({
                    key: 'strings:game.gameState.gameLogs.stolenDevelopmentCard.thief',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardDescriptionLink: CARD_TOOLTIP_LINK_PREFIX + gameLogData.text.cardEnum.toString(),
                        card: convertCardsToChatImageString([gameLogData.text.cardEnum]),
                    },
                },
                {content: CARD_TIP_TITLE[gameLogData.text.cardEnum]}),
            }
        case GameLogType.StolenDevelopmentCardVictim:
            return {
                text: getNestedTranslation({
                    key: 'strings:game.gameState.gameLogs.stolenDevelopmentCard.victim',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardDescriptionLink: CARD_TOOLTIP_LINK_PREFIX + gameLogData.text.cardEnum.toString(),
                        card: convertCardsToChatImageString([gameLogData.text.cardEnum]),
                    },
                },
                {content: CARD_TIP_TITLE[gameLogData.text.cardEnum]}),
            }
        case GameLogType.StolenDevelopmentCardClosed:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.stolenDevelopmentCard.closed',
                    options: {
                        playerNameThief: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorThief),
                        playerNameVictim: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorVictim),
                        cardDescriptionLink: CARD_TOOLTIP_LINK_PREFIX + gameLogData.text.cardBack.toString(),
                        card: convertCardsToChatImageString([gameLogData.text.cardBack]),
                    },
                },
            }
        case GameLogType.PlayerPlayedDevelopmentCard:
            return {
                text: getNestedTranslation({
                    key: 'strings:game.gameState.gameLogs.playerPlayedDevelopmentCard',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        url: `${CARD_TOOLTIP_LINK_PREFIX}${gameLogData.text.cardEnum}`,
                        cardImage: convertCardsToChatImageString([gameLogData.text.cardEnum]),
                    },
                },
                {content: CARD_TIP_TITLE[gameLogData.text.cardEnum]}),
            }
        case GameLogType.YearOfPlentyTookFromBank:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerTookFromBank',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardString: convertCardsToChatImageString(gameLogData.text.cardEnums),
                    },
                },
            }
        case GameLogType.LeftTheGame:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerLeftTheGame',
                    options: {playerUsername: getPlayerUsernameByColor(gameValidator, gameLogData.text.playerColor)},
                },
                italics: true,
                colorAndBoldUsernames: false,
            }
        case GameLogType.WasInactiveAndKicked:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerWasInactiveAndKicked',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.Disconnected:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerDisconnected',
                    options: {playerUsername: getPlayerUsernameByColor(gameValidator, gameLogData.text.playerColor)},
                },
                italics: true,
                colorAndBoldUsernames: false,
            }
        case GameLogType.StoppedSpectating:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.userStoppedSpectating',
                    options: {username: gameLogData.text.username},
                },
            }
        case GameLogType.KarmaPenaltyRemoved:
            return {
                text: {key: 'strings:game.gameState.gameLogs.karmaPenaltyRemoved'},
            }
        case GameLogType.KarmaSystemDisabledDueToPlayersLeaving:
            return {
                text: {key: 'strings:game.gameState.gameLogs.karmaSystemDisabledDueToPlayersLeaving'},
            }
        case GameLogType.KarmaSystemInactiveCannotVoteToDisable:
            return {
                text: {key: 'strings:game.gameState.gameLogs.karmaSystemInactiveCannotVoteToDisable'},
            }
        case GameLogType.KarmaSystemIsAlreadyDisabled:
            return {
                text: {key: 'strings:game.gameState.gameLogs.karmaSystemIsAlreadyDisabled'},
            }
        case GameLogType.YouCantStartVoteBecauseYouAreTheOnlyPlayerLeft:
            return {
                text: {key: 'strings:game.gameState.gameLogs.youCantStartVoteBecauseYouAreTheOnlyPlayerLeft'},
            }
        case GameLogType.YouCantStartVoteDueToYourLeaverStatus:
            return {
                text: {key: 'strings:game.gameState.gameLogs.youCantStartVoteDueToYourLeaverStatus'},
            }
        case GameLogType.YouCanOnlyStartAVoteToDisableKarmaOnce:
            return {
                text: {key: 'strings:game.gameState.gameLogs.youCanOnlyStartAVoteToDisableKarmaOnce'},
            }
        case GameLogType.KarmaSystemVoteToDisableSucceeded:
            return {
                text: {key: 'strings:game.gameState.gameLogs.karmaSystemVoteToDisableSucceeded'},
            }
        case GameLogType.KarmaSystemVoteToDisableDeclined:
            return {
                text: {key: 'strings:game.gameState.gameLogs.karmaSystemVoteToDisableDeclined'},
            }
        case GameLogType.ThereIsAlreadyAnActiveVote:
            return {
                text: {key: 'strings:game.gameState.gameLogs.thereIsAlreadyAnActiveVote'},
            }
        case GameLogType.PlayerStartedAVoteToDisableKarma:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerStartedAVoteToDisableKarma',
                    options: {playerUsername: getPlayerUsernameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.GameEndMessage:
            return {
                text: {key: 'strings:colonistCommon.serverStatus.gameEndMessage'},
            }
        case GameLogType.GameEndChatDisabled:
            return {
                text: {key: 'strings:colonistCommon.serverStatus.gameEndChatDisabled'},
            }
        case GameLogType.PlayerCouldNotStealFrom:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerCouldNotStealFrom',
                    options: {
                        cardString: convertCardsToChatImageString([CardEnum.ResourceBack]),
                        playerNameStealing: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorStealing),
                        playerNameVictim: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorVictim),
                    },
                },
            }
        case GameLogType.GameCrashedZero:
            return {
                text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: '======================='}},
            }
        case GameLogType.GameCrashedOne:
            return {
                text: {key: 'strings:game.gameState.gameLogs.gameCrashed.one'},
            }
        case GameLogType.GameCrashedTwo:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.gameCrashed.two',
                    options: {
                        gameSettingId: uiGameManager.gameController.gameSettings.id,
                        dateString: gameLogData.text.dateString,
                    },
                },
            }
        case GameLogType.GameCrashedThree:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.gameCrashed.three',
                    options: {email: SharedStrings.SUPPORT_EMAIL, discordUrl: SharedStrings.DISCORD_CHANNEL},
                },
            }
        case GameLogType.Separator:
            return {
                text: {
                    key: 'strings:colonistCommon.utils.#buffer',
                    options: {value: '<hr>', interpolation: {escapeValue: false}},
                },
            }
        case GameLogType.PlayerWonTheGame:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerWonTheGame',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
                isCentered: true,
            }
        case GameLogType.GameEndedDueToServerRestart:
            return {
                text: {key: 'strings:game.gameState.gameLogs.gameEndedDueToServerRestart'},
            }
        case GameLogType.ResourceDistribution:
            const options = {
                playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                cardsString: convertCardsToChatImageString(gameLogData.text.cardsToBroadcast),
            }
            switch(gameLogData.text.distributionType) {
                case CardDistributionType.StartingResources:
                    return {text: {key: 'strings:game.gameState.gameLogs.playerReceivedStartingResources', options: options}}
                case CardDistributionType.ResourceTile:
                    return {text: {key: 'strings:game.gameState.gameLogs.playerGotCards', options: options}}
                case CardDistributionType.GoldTile:
                    return {text: {key: 'strings:game.gameState.gameLogs.playerSelectedFromGoldTile', options: options}}
                case CardDistributionType.Aqueduct:
                    return {text: {key: 'strings:game.gameState.gameLogs.playerSelectedFromAqueduct', options: options}}
                default:
                    return {text: {key: 'strings:colonistCommon.utils.emptyString'}}
            }
        case GameLogType.NotEnoughCardsInBankToDistribute:
            const optionsNotEnoughCards = {
                cardString: CardEnum[gameLogData.text.cardEnum],
                bankCount: gameLogData.text.bankCount,
                ...(gameLogData.text.count != undefined ? {count: gameLogData.text.count} : undefined),
            }
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.notEnoughCardsInBankToDistribute',
                    options: optionsNotEnoughCards,
                },
            }
        case GameLogType.TileBlockedByRobber:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.tileBlockedByRobber',
                    options: {tileString: getTileChatString(gameLogData.text.tileInfo)},
                },
            }
        case GameLogType.PlayersSelectingResourcesFromAqueduct:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playersSelectingResourcesFromAqueduct',
                    options: {playerUsernames: getUsernamesByColors(gameValidator, gameLogData.text.playerColors)},
                },
            }
        case GameLogType.BotSelectingAqueductResourcesFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botSelectingAqueductResourcesFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.NotEnoughProgressCardsInBankToDistribute:
            return {
                text: {key: 'strings:game.gameState.gameLogs.notEnoughProgressCardsInBankToDistribute'},
            }
        case GameLogType.PlayersDiscardingProgressCards:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playersDiscardingProgressCards',
                    options: {playerUsernames: getUsernamesByColors(gameValidator, gameLogData.text.playerColors)},
                },
            }
        case GameLogType.PlayerDiscarded:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerDiscarded',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardString: convertCardsToChatImageString(gameLogData.text.cardEnums),
                    },
                },
            }
        case GameLogType.PlayersSelectingResourceFromGoldTile:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playersSelectingResourceFromGoldTile',
                    options: {playerUsernames: getUsernamesByColors(gameValidator, gameLogData.text.playerColors)},
                },
            }
        case GameLogType.FriendlyPirateActive:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.friendlyPirateActive',
                    options: {context: gameLogData.text.all ? 'all' : undefined},
                },
            }
        case GameLogType.BotIsSelectingWhereToPlacePieceFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingWhereToPlacePieceFor',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                    },
                },
            }
        case GameLogType.RobberDisabledUntilFirstBarbarianAttack:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.robberDisabledUntilFirstBarbarianAttack',
                    options: {robberString: ':robber:'},
                },
            }
        case GameLogType.FriendlyRobberActive:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.friendlyRobberActive',
                    options: {robberString: ':robber:', context: gameLogData.text.all ? 'all' : undefined},
                },
            }
        case GameLogType.LostMetropolis:
            const lostMetropolisText = EnumUtils.getEnumKey(gameLogData.text.metropolisType, CityImprovementTypes)
            return {
                text: {
                    key: `strings:game.gameState.gameLogs.playerLostMetropolis.${lostMetropolisText}`,
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: `:metropolis_${lostMetropolisText}:`,
                    },
                },
            }
        case GameLogType.PlayerUpgradedImprovementTo:
            const cityImprovementIconName = getCityImprovementFramedSquareImageName(gameLogData.text.improvementType)
            const improvementTypeKey = EnumUtils.getEnumKey(gameLogData.text.improvementType, CityImprovementTypes)
            const cityImprovementTitle = clientI18n.t(`strings:game.cards.progress.title.${improvementTypeKey}`)
            const cityImprovementString = `${cityImprovementTitle} :${cityImprovementIconName}:`
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerUpgradedImprovementTo',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cityImprovementString: cityImprovementString,
                        newCityLevel: gameLogData.text.newCityLevel,
                    },
                },
            }
        case GameLogType.BotIsSelectingGoldTileResourceFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingGoldTileResourceFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.BotIsSelectingCardsToDiscardFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingCardsToDiscardFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerSettledOnForeignLand:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerSettledOnForeignLand',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerReceivedAchievement:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerReceivedAchievement',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        achievementString: getAchievementString(gameLogData.text.achievementEnum, translationKeyContext),
                    },
                },
            }
        case GameLogType.PlayerLostAchievement:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerLostAchievement',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        achievementString: getAchievementString(gameLogData.text.achievementEnum, translationKeyContext),
                    },
                },
            }
        case GameLogType.PlayerPassedAchievementTo:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerPassedAchievementTo',
                    options: {
                        playerNameOld: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorOld),
                        playerNameNew: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorNew),
                        achievementString: getAchievementString(gameLogData.text.achievementEnum, translationKeyContext),
                    },
                },
            }
        case GameLogType.PlayerMovedMerchantTo:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerMovedMerchantTo',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        tileChatString: getTileChatString(gameLogData.text.tileInfo),
                        context: gameLogData.text.isMerchantOwnerDisplacingMerchant ? 'owner' : undefined,
                    },
                },
            }
        case GameLogType.PlayerLostPossessionOfMerchant:
            const merchantImageName = getMerchantImageNameForPlayer(gameLogData.text.merchantOwnerColor)
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerLostPossessionOfPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: `:${merchantImageName}:`,
                    },
                },
            }
        case GameLogType.BotIsSelectingWhereToPlaceMerchantFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingWhereToPlaceMerchantFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.BotIsPlacing:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsPlacing',
                    options: {
                        context: getBotIsPlacingContext(gameLogData.text.pieceEnum),
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                    },
                },
            }
        case GameLogType.BotIsSelectingWhoToRobFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingWhoToRobFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.NoPlayerToStealFrom:
            return {
                text: {key: 'strings:game.gameState.gameLogs.noPlayerToStealFrom'},
            }
        case GameLogType.SkippingSpecialBuildPhase:
            return {
                text: {key: 'strings:game.gameState.gameLogs.skippingSpecialBuildPhase'},
            }
        case GameLogType.PlayerEnteredSpecialBuildPhase:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerEnteredSpecialBuildPhase',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.BotIsSelectingProgressCardFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingProgressCardFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerIsSpyingPlayer:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerIsSpyingPlayer',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        playerNameVictim: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorVictim),
                    },
                },
            }
        case GameLogType.PlayersDefeatedBarbarians:
            return {
                text: {key: 'strings:game.gameState.gameLogs.playersDefeatedBarbarians'},
            }
        case GameLogType.PlayerAwardedDefenderOfColonist:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerAwardedDefenderOfColonist',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: ':defenderofcolonist:',
                    },
                },
            }
        case GameLogType.PlayersSelectingProgressCard:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playersSelectingProgressCard',
                    options: {playerUsernames: getUsernamesByColors(gameValidator, gameLogData.text.playerColors)},
                },
            }
        case GameLogType.BotIsSelectingPieceToDestroyFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingPieceToDestroyFor',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                    },
                },
            }
        case GameLogType.BarbariansAttackedButNoCityToPillage:
            return {
                text: {key: 'strings:game.gameState.gameLogs.barbariansAttackedButNoCityToPillage'},
            }
        case GameLogType.PlayersDefeatedByBarbarians:
            return {
                text: {key: 'strings:game.gameState.gameLogs.playersDefeatedByBarbarians'},
            }
        case GameLogType.PlayersPieceDestroyedByBarbarians:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playersPieceDestroyedByBarbarians',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                    },
                },
            }
        case GameLogType.PlayerStoleUsingMonopoly:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerStoleUsingMonopoly',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        amountStolen: gameLogData.text.amountStolen,
                        cardString: CardEnum[gameLogData.text.cardEnum],
                    },
                },
            }
        case GameLogType.BotIsPlacingPieceFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsPlacingPieceFor',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: getPieceStringFromPieceEnum(gameLogData.text.pieceEnum),
                    },
                },
            }
        case GameLogType.BotIsSelectingWhereToPlaceKnightFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botIsSelectingWhereToPlaceKnightFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerUsedPieceToMovePiece:
            const knightImageName = getKnightImageName(gameLogData.text.knightInfo)
            const movedPieceString = gameLogData.text.isMovedPieceRobber ? 'robber' : 'pirate'
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerUsedPieceToMovePiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: `:${knightImageName}:`,
                        movedPieceString: movedPieceString,
                    },
                },
            }
        case GameLogType.PlayerDisplacedPiece:
            const displacerKnightImageName = getKnightImageName(gameLogData.text.displacerKnightInfo)
            const enemyKnightImageName = getKnightImageName(gameLogData.text.enemyKnightInfo)
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerDisplacedPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        pieceString: `:${displacerKnightImageName}:`,
                        enemyPieceString: `:${enemyKnightImageName}:`,
                    },
                },
            }
        case GameLogType.BotSelectingWhereToMoveKnightFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botSelectingWhereToMoveKnightFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerRemovedPlayersKnight:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerRemovedPlayersKnight',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        playerNameLosingKnight: getPlayerDisplayNameByColor(gameValidator,
                            gameLogData.text.playerColorLosingKnight),
                    },
                },
            }
        case GameLogType.BotSelectingKnightToRemoveFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botSelectingKnightToRemoveFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.BotSelectingKnightToUpgradeFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botSelectingKnightToUpgradeFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerReceivedVPProgressCard:
            return {
                text: getNestedTranslation({
                    key: 'strings:game.gameState.gameLogs.playerReceivedVPProgressCard',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                    },
                },
                {progressCardName: CARD_TIP_TITLE[gameLogData.text.cardEnum]}),
            }
        case GameLogType.PlayerSelectedPlayer:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerSelectedPlayer',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        playerNameSelected: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorSelected),
                    },
                },
            }
        case GameLogType.PlayerRepositioningEdgePiece:
            const repositioningEdgeTypeText = EnumUtils.getEnumKey(gameLogData.text.edgeType, TileEdgeType)
            return {
                text: {
                    key: `strings:game.gameState.gameLogs.playerRepositioningEdgePiece.${repositioningEdgeTypeText}`,
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerRemovedEdgePiece:
            const pieceRemovedEdgeTypeText = EnumUtils.getEnumKey(gameLogData.text.edgeType, TileEdgeType)
            return {
                text: {
                    key: `strings:game.gameState.gameLogs.playerRemovedEdgePiece.${pieceRemovedEdgeTypeText}`,
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        playerNameLosingPiece: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorLosingPiece),
                        context: gameLogData.text.playerColor == gameLogData.text.playerColorLosingPiece ? 'own' : undefined,
                    },
                },
            }
        case GameLogType.BotIsSelectingEdgePieceToRemoveFor:
            const botSelectingEdgeTypeText = EnumUtils.getEnumKey(gameLogData.text.edgeType, TileEdgeType)
            return {
                text: {
                    key: `strings:game.gameState.gameLogs.botIsSelectingEdgePieceToRemoveFor.${botSelectingEdgeTypeText}`,
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.BotSelectingResourcesFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botSelectingResourcesFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.BotSelectingDiceFor:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.botSelectingDiceFor',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerSwappedTileNumbers:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerSwappedTileNumbers',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        firstTileChatString: getTileChatString(gameLogData.text.firstTileInfo),
                        secondTileChatString: getTileChatString(gameLogData.text.secondTileInfo),
                    },
                },
            }
        case GameLogType.ExchangedCardsThief:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.exchangedCards.thief',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        givenCardString: CardEnum[gameLogData.text.givenCardEnum],
                        receivedCardString: CardEnum[gameLogData.text.receivedCardEnum],
                    },
                },
            }
        case GameLogType.ExchangedCardsVictim:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.exchangedCards.victim',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        givenCardString: CardEnum[gameLogData.text.givenCardEnum],
                        receivedCardString: CardEnum[gameLogData.text.receivedCardEnum],
                    },
                },
            }
        case GameLogType.ExchangedCardsClosed:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.exchangedCards.closed',
                    options: {
                        playerNameThief: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorThief),
                        playerNameVictim: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorVictim),
                    },
                },
            }
        case GameLogType.PlayerIsStealingFrom:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerIsStealingFrom',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        playerNameVictim: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorVictim),
                    },
                },
            }
        case GameLogType.PlayerSelectedCard:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerSelectedCard',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        cardString: CardEnum[gameLogData.text.cardEnum],
                    },
                },
            }
        case GameLogType.BotLogicNotSupportedForCitiesAndKnights:
            return {
                text: {key: 'strings:game.gameState.gameLogs.botLogicNotSupportedForCitiesAndKnights'},
            }
        case GameLogType.PlayerUnlockedAqueduct:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerUnlockedAqueduct',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerUnlockedTradingHouse:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerUnlockedTradingHouse',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerUnlockedFortress:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerUnlockedFortress',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerResigned:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerResigned',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayerStartedEmbargo:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerStartedEmbargo',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        embargoedPlayerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.embargoedPlayerColor),
                    },
                },
            }
        case GameLogType.PlayerEndedEmbargo:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerEndedEmbargo',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        embargoedPlayerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.embargoedPlayerColor),
                    },
                },
            }
        case GameLogType.PlayerTradedWithPlayer:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerTradedWithPlayer',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        acceptingPlayerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.acceptingPlayerColor),
                        givenCardString: convertCardsToChatImageString(gameLogData.text.givenCardEnums),
                        receivedCardString: convertCardsToChatImageString(gameLogData.text.receivedCardEnums),
                    },
                },
            }
        case GameLogType.PlayerTradedWithBank:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerTradedWithBank',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        givenCardString: convertCardsToChatImageString(gameLogData.text.givenCardEnums),
                        receivedCardString: convertCardsToChatImageString(gameLogData.text.receivedCardEnums),
                    },
                },
            }
        case GameLogType.PlayerWantsToCounterOfferWith:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerWantsToCounterOfferWith',
                    options: {
                        playerNameCreator: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorCreator),
                        playerNameOffered: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColorOffered),
                        wantedCardString: convertCardsToChatImageString(gameLogData.text.wantedCardEnums),
                        offeredCardString: convertCardsToChatImageString(gameLogData.text.offeredCardEnums),
                    },
                },
            }
        case GameLogType.PlayerWantsToTradeWith:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerWantsToTradeWith',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor),
                        wantedCardString: convertCardsToChatImageString(gameLogData.text.wantedCardEnums),
                        offeredCardString: convertCardsToChatImageString(gameLogData.text.offeredCardEnums),
                    },
                },
            }
        case GameLogType.PlayerCreatedRematch:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerCreatedRematch',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PlayersJoinedRematch:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playersJoinedRematch',
                    options: {count: gameLogData.text.count, playerUsernames: gameLogData.text.playerUsernames},
                },
            }
        case GameLogType.YouCanOnlyStartAVoteToPauseXTimes:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.youCanOnlyStartAVoteToPauseXTimes',
                    options: {count: gameLogData.text.count},
                },
            }
        case GameLogType.GamePaused:
            return {
                text: {key: 'strings:game.gameState.gameLogs.gamePaused'},
            }
        case GameLogType.GameResumed:
            return {
                text: {key: 'strings:game.gameState.gameLogs.gameResumed'},
            }
        case GameLogType.PauseSystemPlayerStartedVotePause:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.pauseSystemPlayerStartedVote.pause',
                    options: {playerUsername: getPlayerUsernameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PauseSystemPlayerStartedVoteResume:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.pauseSystemPlayerStartedVote.resume',
                    options: {playerUsername: getPlayerUsernameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
        case GameLogType.PauseSystemVoteSucceededPause:
            return {
                text: {key: 'strings:game.gameState.gameLogs.pauseSystemVoteSucceeded.pause'},
            }
        case GameLogType.PauseSystemVoteSucceededResume:
            return {
                text: {key: 'strings:game.gameState.gameLogs.pauseSystemVoteSucceeded.resume'},
            }
        case GameLogType.PauseSystemVoteDeclinedPause:
            return {
                text: {key: 'strings:game.gameState.gameLogs.pauseSystemVoteDeclined.pause'},
            }
        case GameLogType.PauseSystemVoteDeclinedResume:
            return {
                text: {key: 'strings:game.gameState.gameLogs.pauseSystemVoteDeclined.resume'},
            }
        case GameLogType.YouAreLastPlayerYouWillWinIfOpponentDoesNotReconnect:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.youAreLastPlayerYouWillWinIfOpponentDoesNotReconnect',
                    options: {count: gameLogData.text.count},
                },
            }
        case GameLogType.VoteHasBeenCanceledBecauseGameEnded:
            return {
                text: {key: 'strings:game.gameState.gameLogs.voteHasBeenCanceledBecauseGameEnded'},
            }
        case GameLogType.PlayerPlacedKnight:
            const placedKnightImageName = getKnightImageName(gameLogData.text.knightInfo)
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerPlacedPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.knightInfo.ownerColor),
                        pieceString: `:${placedKnightImageName}:`,
                    },
                },
            }
        case GameLogType.PlayerMovedKnight:
            const movedKnightImageName = getKnightImageName(gameLogData.text.knightInfo)
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.playerMovedPiece',
                    options: {
                        playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.knightInfo.ownerColor),
                        pieceString: `:${movedKnightImageName}:`,
                        context: 'colon',
                    },
                },
            }
        case GameLogType.ChatCommandGetAllResources:
            return {
                text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 'Admin got all 5 kinds of resources'}},
            }
        case GameLogType.ChatCommandGetCard:
            return {
                text: {
                    key: 'strings:colonistCommon.utils.#buffer',
                    options: {value: 'Admin got ' + CardEnum[gameLogData.text.cardEnum]},
                },
            }
        case GameLogType.ChatCommandRollAll:
            return {
                text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 'Admin gave resources for all tiles'}},
            }
        case GameLogType.ChatCommandRollDice:
            return {
                text: {
                    key: 'strings:colonistCommon.utils.#buffer',
                    options: {value: 'Admin gave resources for dice ' + gameLogData.text.diceNumber},
                },
            }
        case GameLogType.ChatCommandRollNextDice:
            return {
                text: {
                    key: 'strings:colonistCommon.utils.#buffer',
                    options: {
                        value: 'Admin decided to roll: ' + gameLogData.text.diceNumber1 + ' ' + gameLogData.text.diceNumber2,
                    },
                },
            }
        case GameLogType.CantStealPlayersDontHaveCards:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.cantStealPlayersDontHaveCards',
                    options: {count: gameLogData.text.playerCount},
                },
            }
        case GameLogType.PermanentlyKickedForGoingAFKTooManyTime:
            return {
                text: {
                    key: 'strings:game.gameState.gameLogs.permanentlyKickedForGoingAFKTooManyTime',
                    options: {playerName: getPlayerDisplayNameByColor(gameValidator, gameLogData.text.playerColor)},
                },
            }
    }
}

function convertCardsToChatImageString(cardEnums: CardEnum[]): string {
    return cardEnums
        .map((card) => `:card_${card}:`)
        .join(' ')
}
